import {
  ACCEPTED_FILE_TYPES,
  GetUploadUrlRequest,
  MAX_VIDEO_FILE_SIZE,
} from '@eigtech/documents-types'
import { randomString } from '@eigtech/function-utils'
import { Corn } from '@eigtech/shared-corn'
import { useUploadMultipleDocuments } from '@eigtech/ui-shared-documents'
import { FileEntry, FileUpload, GalleryUploadForm } from '@eigtech/ui-shared-files'
import { createForm } from '@eigtech/ui-shared-forms/shadcn'
import { useRef } from 'react'
import { FileUploadForm, FileUploadSchema, maximumUploadsCount } from './schema'

export type UploadDocumentsProps = {
  entityId: Corn
  uploadFormId?: string
} & Pick<GetUploadUrlRequest, 'cohort'>

export function UploadDocuments({ cohort, entityId, uploadFormId }: UploadDocumentsProps) {
  const form = useForm({ defaultValues: { files: [] } })

  const {
    mutation: { mutateAsync: uploadDocuments, isError: isUploadError, error: uploadError, reset },
    numberOfFilesUploaded,
    numberOfFiles,
    isPendingUploadUrl,
    isUploadUrlError,
  } = useUploadMultipleDocuments({ entityId, cohort })

  // Separate ref holds the actual File objects — cleared as soon as each upload starts
  const fileRefs = useRef<Map<string, File>>(new Map())

  async function onSubmit({ files }: FileUploadForm) {
    reset()

    const filesToUpload: FileUpload[] = files
      .map(({ file, ...meta }) => {
        const parts = file.name.split('.')
        const extension = parts.pop()
        const noExtension = parts.join('.')
        const notes = (meta.notes ?? '').trim() || noExtension
        meta.title = (meta.title ?? '').trim() || notes
        meta.notes = `${notes}-${randomString()}`
        const newFilename = `${meta.notes}.${extension}`

        const fileToUpload = file.getFile()
        if (!fileToUpload) {
          return
        }

        fileRefs.current.set(
          file.id,
          new File([fileToUpload], newFilename, {
            lastModified: Date.now(),
            type: file.type,
          })
        )

        const updatedFile: FileEntry = {
          ...file,
          name: newFilename,
          getFile: () => fileRefs.current.get(file.id) ?? null,
          revokeFile: () => {
            file.revokeFile()
            fileRefs.current.delete(file.id)
          },
        }

        return {
          file: updatedFile,
          meta,
        }
      })
      .filter((file) => !!file)

    await uploadDocuments(filesToUpload)
  }

  return (
    <GalleryUploadForm<typeof FileUploadSchema>
      ArrayField={ArrayField}
      GalleryItemFields={ImageFields}
      accept={ACCEPTED_FILE_TYPES.join(',')}
      acceptedTypesLabel="Video, Image, PDF, Word, Excel, Xactimate"
      form={form}
      formId={uploadFormId ?? 'uploadDocumentsForm'}
      isPendingUploadUrl={isPendingUploadUrl}
      isUploadError={isUploadError}
      isUploadUrlError={isUploadUrlError}
      maxFiles={maximumUploadsCount}
      maxSize={MAX_VIDEO_FILE_SIZE}
      numberOfFiles={numberOfFiles}
      numberOfFilesUploaded={numberOfFilesUploaded}
      uploadError={uploadError}
      onSubmit={onSubmit}
    />
  )
}

function ArrayField({ children }: { children: React.ReactNode }) {
  return (
    <BaseArrayField emptyMessage="" label="" name="files">
      {children}
    </BaseArrayField>
  )
}

function ImageFields() {
  const { name } = useArrayFieldRowContext()

  return (
    <>
      <InputField label="Title" name={`${name}.title`} />
      <TextareaField label="File Description" name={`${name}.description`} rows={5} />
    </>
  )
}

const {
  useForm,
  ArrayField: BaseArrayField,
  InputField,
  TextareaField,
  useArrayFieldRowContext,
} = createForm(FileUploadSchema, 'fileUploadForm')
